import React, { useContext, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Button, Alert, Spinner, Modal, RadioGroup,
} from '@printdeal/ui-library';

import { ErrorManagement } from '@printdeal/error-management/react';
import { SanitizedOrder } from '../../../../../../types/order/Order';
import { ServiceFactory } from '../../../../../../services/ServiceFactory';
// @ts-expect-error
import { getPaymentMethod, paymentMethodIds } from '../../../../../../helpers/payments/paymentMethods';

import PaymentMethodOption from './PaymentMethodOption';
import { dispatchAccountEvent } from '../../../../../../helpers/dataLayerHelpers/accountDataLayerHelper';
import OrderDetailContext from '../../../contexts/OrderDetailContext';

enum States {
  IDLE = 'idle',
  LOADING = 'loading',
  ERROR = 'error',
}

interface ChangePaymentMethodModalProps {
  order: SanitizedOrder;
  handleCloseModal: (eventName: string) => void;
}

const ChangePaymentMethodModal = ({
  order,
  handleCloseModal,
}: ChangePaymentMethodModalProps) => {
  const { reFetchOrder } = useContext(OrderDetailContext);
  const paymentMethod = order.paymentInfo ? getPaymentMethod(order.paymentInfo) : paymentMethodIds.ideal;

  const intl = useIntl();
  const queryClient = useQueryClient();
  const [changePaymentMethodState, setChangePaymentMethodState] = useState(States.IDLE);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(paymentMethod);

  const paymentMethods = useQuery({
    queryKey: ['order', 'get-payment-methods', order.id],
    queryFn: async () => {
      try {
        const PaymentMethodsQuery = await ServiceFactory.createPaymentV2Service()
          .getPaymentMethodsForOrder(order.id);

        return PaymentMethodsQuery.methods;
      } catch (error) {
        ErrorManagement.exception({
          exception: error,
          tags: {
            fileLocation: 'ChangePaymentMethod',
            featureFunction: 'ChangePaymentMethodModal',
          },
          fingerprint: ['order-detail-page-get-payment-methods-in-modal'],
        });

        dispatchAccountEvent('order_detail_api_failure', 'error_get_payment_methods');
        return [];
      }
    },
    staleTime: 30000000,
  });

  if (!reFetchOrder) {
    return null;
  }

  const handleChangePaymentMethod = async () => {
    if (!selectedPaymentMethod) {
      return;
    }

    setChangePaymentMethodState(States.LOADING);

    try {
      await queryClient.fetchQuery({
        queryKey: ['order', 'change-payment-methods', order.id, order.version],
        queryFn: () => ServiceFactory.createPaymentV2Service()
          .changePaymentMethodOfOrder(selectedPaymentMethod, order.id),
      });

      await reFetchOrder({ reFetchMandate: true, reFetchPaymentLink: true });

      handleCloseModal('edit_payment_method');
    } catch (error) {
      ErrorManagement.exception({
        exception: error,
        tags: {
          fileLocation: 'ChangePaymentMethodModal',
          featureFunction: 'handleChangePaymentMethod',
        },
        fingerprint: ['order-detail-page-change-payment-method'],
      });

      dispatchAccountEvent('order_detail_api_failure', 'error_edit_payment_method');
      setChangePaymentMethodState(States.ERROR);
    }
  };

  return (
    <Modal
      isOpen
      onClose={() => handleCloseModal('close_modal_edit_payment_method_generic')}
      dismissLabel={intl.formatMessage({
        id: 'account.general.modal.close',
        defaultMessage: 'Close',
      })}
      title={intl.formatMessage({
        id: 'orderDetails.paymentMethodModal.modal.title',
        defaultMessage: 'Change payment method',
      })}
    >
      <Modal.Content data-cy="changePaymentMethodModal">
        <>
          {paymentMethods?.isError && (
            <Alert variant="error" className="pd-mb-3">
              <FormattedMessage id="orderDetails.fetchPaymentMethod.error" defaultMessage="The payment methods could not be loaded. Please try again later." />
            </Alert>
          )}
          {paymentMethods?.isLoading && (
            <div className="pd-text-center">
              <FormattedMessage id="orderDetails.fetchPaymentMethod.loading" defaultMessage="The payment methods are being loaded." />
              <Spinner
                variant="dim"
                size="lg"
                screenReaderLabel={intl.formatMessage({ id: 'uilib.spinner.loading', defaultMessage: 'Loading...' })}
              />
            </div>
          )}
          {changePaymentMethodState === States.ERROR && (
            <Alert variant="error" className="pd-mb-3">
              <FormattedMessage id="orderDetails.paymentMethod.error" defaultMessage="error changing payment method" />
            </Alert>
          )}
          <RadioGroup id="paymentMethod" name="paymentMethod">
            {paymentMethods?.data?.map((method) => (
              <PaymentMethodOption
                paymentMethod={method}
                selectedPaymentMethod={selectedPaymentMethod}
                setSelectedPaymentMethod={setSelectedPaymentMethod}
                key={method.id}
              />
            ))}
          </RadioGroup>
        </>
      </Modal.Content>
      <Modal.Footer
        className="pd-flex pd-flex-row-reverse pd-flex-wrap pd-gap-2"
      >
        <Button
          data-cy="confirmChangePaymentMethodButton"
          variant="primary"
          onClick={handleChangePaymentMethod}
          disabled={paymentMethods.isLoading || paymentMethods.isError || changePaymentMethodState === States.LOADING}
        >
          {changePaymentMethodState === States.LOADING && (
            <Spinner
              variant="dim"
              size="md"
              screenReaderLabel={intl.formatMessage({ id: 'uilib.spinner.loading', defaultMessage: 'Loading...' })}
            />
          )}
          <FormattedMessage
            id="orderDetails.paymentMethodModal.modal.Button"
            defaultMessage="Change payment method"
          />
        </Button>
        <Button
          variant="secondary"
          onClick={() => handleCloseModal('close_modal_edit_payment_method_by_button')}
          data-cy="cancelButtonChangePaymentMethod"
        >
          <FormattedMessage
            id="orderDetails.paymentMethodModal.modal.cancelButton"
            defaultMessage="Close"
          />
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ChangePaymentMethodModal;
